import React, { useState, useRef, useEffect } from 'react';
import { stylistAPI } from '../../../../services/api';
import { X, Plus, Loader2, MessageSquare } from 'lucide-react';

const AddLookModal = ({ clientId, wardrobeItems, onClose, onAdded, requests = [] }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    wardrobe_item_ids: [],
    look_images: [],
    event_types: [],
    request_id: null
  });
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [eventInput, setEventInput] = useState('');
  const [availableRequests, setAvailableRequests] = useState(requests);
  const fileInputRef = useRef(null);

  useEffect(() => {
    // Fetch requests if not provided
    if (requests.length === 0) {
      stylistAPI.getClientRequests(clientId).then(setAvailableRequests).catch(console.error);
    }
  }, [clientId, requests]);

  // Filter only look-type requests that are not completed
  const lookRequests = availableRequests.filter(r => r.request_type === 'look' && r.status !== 'completed');

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const response = await stylistAPI.uploadImage(file);
      const fullUrl = response.url;
      setFormData({ ...formData, look_images: [...formData.look_images, fullUrl] });
    } catch (err) {
      console.error('Upload error:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleAddEvent = () => {
    if (eventInput.trim() && !formData.event_types.includes(eventInput.trim())) {
      setFormData({ ...formData, event_types: [...formData.event_types, eventInput.trim()] });
      setEventInput('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      alert('Введите название образа');
      return;
    }

    setLoading(true);
    try {
      await stylistAPI.createLook(clientId, {
        ...formData,
        client_id: clientId,
        request_id: formData.request_id || null
      });
      
      // If linked to a request, update request status to completed
      if (formData.request_id) {
        try {
          await stylistAPI.updateRequestStatus(formData.request_id, 'completed');
        } catch (err) {
          console.error('Error updating request status:', err);
        }
      }
      
      onAdded();
    } catch (err) {
      console.error('Error creating look:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div onClick={onClose} style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 'var(--space-l)', zIndex: 1000 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ backgroundColor: 'var(--white)', padding: 'var(--space-xxl)', maxWidth: '600px', width: '100%', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 'var(--space-xl)' }}>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: '1.5rem' }}>Создать образ</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Name */}
          <div style={{ marginBottom: 'var(--space-l)' }}>
            <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
              Название *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Деловая встреча, вечерний выход..."
              style={{ width: '100%', padding: '12px', fontFamily: 'var(--font-body)', fontSize: '0.9375rem', border: '1px solid var(--soft-gray)', boxSizing: 'border-box' }}
            />
          </div>

          {/* Description */}
          <div style={{ marginBottom: 'var(--space-l)' }}>
            <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
              Описание
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={2}
              style={{ width: '100%', padding: '12px', fontFamily: 'var(--font-body)', fontSize: '0.9375rem', border: '1px solid var(--soft-gray)', boxSizing: 'border-box', resize: 'vertical' }}
            />
          </div>

          {/* Look Image */}
          <div style={{ marginBottom: 'var(--space-l)' }}>
            <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
              Фото образа
            </label>
            <input type="file" ref={fileInputRef} onChange={handleFileChange} accept="image/*" style={{ display: 'none' }} />
            <div style={{ display: 'flex', gap: 'var(--space-s)', flexWrap: 'wrap' }}>
              {formData.look_images.map((img, idx) => (
                <div key={idx} style={{ position: 'relative', width: '80px', height: '80px' }}>
                  <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, look_images: formData.look_images.filter((_, i) => i !== idx) })}
                    style={{ position: 'absolute', top: '2px', right: '2px', padding: '2px', backgroundColor: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                  >
                    <X size={12} />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                style={{
                  width: '80px',
                  height: '80px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'var(--cream)',
                  border: '2px dashed var(--soft-gray)',
                  cursor: 'pointer'
                }}
              >
                {uploading ? <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} /> : <Plus size={20} style={{ color: 'var(--mid-gray)' }} />}
              </button>
            </div>
          </div>

          {/* Select Wardrobe Items */}
          <div style={{ marginBottom: 'var(--space-l)' }}>
            <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
              Вещи в образе ({formData.wardrobe_item_ids.length})
            </label>
            {formData.wardrobe_item_ids.length === 0 ? (
              <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', color: 'var(--light-gray)', fontStyle: 'italic' }}>
                Выберите вещи из гардероба ниже
              </p>
            ) : (
              <div style={{ display: 'flex', gap: 'var(--space-s)', flexWrap: 'wrap' }}>
                {formData.wardrobe_item_ids.map((itemId) => {
                  const item = wardrobeItems.find(w => w.id === itemId);
                  if (!item) return null;
                  return (
                    <div key={itemId} style={{ position: 'relative', width: '70px' }}>
                      <div style={{ width: '70px', height: '70px', border: '2px solid var(--accent-gold)', overflow: 'hidden' }}>
                        <img src={item.image_url} alt={item.item_kind} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, wardrobe_item_ids: formData.wardrobe_item_ids.filter(id => id !== itemId) })}
                        style={{
                          position: 'absolute',
                          top: '-6px',
                          right: '-6px',
                          width: '20px',
                          height: '20px',
                          borderRadius: '50%',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          padding: 0
                        }}
                      >
                        <X size={12} />
                      </button>
                      <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.6875rem', textAlign: 'center', marginTop: '4px', textTransform: 'capitalize', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {item.item_kind}
                      </p>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Available items to add */}
          <div style={{ marginBottom: 'var(--space-l)' }}>
            <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
              Добавить вещь из гардероба
            </label>
            <div style={{ display: 'flex', gap: 'var(--space-s)', flexWrap: 'wrap' }}>
              {wardrobeItems.filter(item => !formData.wardrobe_item_ids.includes(item.id)).map((item) => (
                <div
                  key={item.id}
                  onClick={() => setFormData({ ...formData, wardrobe_item_ids: [...formData.wardrobe_item_ids, item.id] })}
                  style={{ width: '60px', cursor: 'pointer' }}
                >
                  <div style={{ width: '60px', height: '60px', border: '2px solid var(--soft-gray)', overflow: 'hidden', transition: 'border-color 200ms' }}
                    onMouseEnter={(e) => e.currentTarget.style.borderColor = 'var(--accent-gold)'}
                    onMouseLeave={(e) => e.currentTarget.style.borderColor = 'var(--soft-gray)'}
                  >
                    <img src={item.image_url} alt={item.item_kind} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                  <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.625rem', textAlign: 'center', marginTop: '2px', color: 'var(--mid-gray)', textTransform: 'capitalize', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {item.item_kind}
                  </p>
                </div>
              ))}
              {wardrobeItems.filter(item => !formData.wardrobe_item_ids.includes(item.id)).length === 0 && (
                <p style={{ fontFamily: 'var(--font-body)', fontSize: '0.875rem', color: 'var(--light-gray)', fontStyle: 'italic' }}>
                  Все вещи добавлены
                </p>
              )}
            </div>
          </div>

          {/* Event Types */}
          <div style={{ marginBottom: 'var(--space-l)' }}>
            <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
              Для каких событий
            </label>
            <div style={{ display: 'flex', gap: 'var(--space-s)', marginBottom: 'var(--space-s)' }}>
              <input
                type="text"
                value={eventInput}
                onChange={(e) => setEventInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddEvent())}
                placeholder="работа, свидание, прогулка..."
                style={{ flex: 1, padding: '10px', fontFamily: 'var(--font-body)', fontSize: '0.875rem', border: '1px solid var(--soft-gray)' }}
              />
              <button type="button" onClick={handleAddEvent} style={{ padding: '10px 16px', backgroundColor: 'var(--cream)', border: 'none', cursor: 'pointer' }}>
                <Plus size={16} />
              </button>
            </div>
            <div style={{ display: 'flex', gap: 'var(--space-s)', flexWrap: 'wrap' }}>
              {formData.event_types.map((event, idx) => (
                <span key={idx} style={{ padding: '4px 10px', backgroundColor: 'var(--cream)', fontSize: '0.8125rem', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  {event}
                  <button type="button" onClick={() => setFormData({ ...formData, event_types: formData.event_types.filter((_, i) => i !== idx) })} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Link to Request */}
          {lookRequests.length > 0 && (
            <div style={{ marginBottom: 'var(--space-l)' }}>
              <label style={{ display: 'block', fontFamily: 'var(--font-body)', fontSize: '0.75rem', letterSpacing: '1px', textTransform: 'uppercase', color: 'var(--mid-gray)', marginBottom: 'var(--space-s)' }}>
                Привязать к запросу клиента
              </label>
              <select
                value={formData.request_id || ''}
                onChange={(e) => setFormData({ ...formData, request_id: e.target.value || null })}
                style={{
                  width: '100%',
                  padding: '12px',
                  fontFamily: 'var(--font-body)',
                  fontSize: '0.9375rem',
                  border: '1px solid var(--soft-gray)',
                  backgroundColor: 'var(--white)',
                  cursor: 'pointer'
                }}
              >
                <option value="">— Не привязывать —</option>
                {lookRequests.map((req) => (
                  <option key={req.id} value={req.id}>
                    {req.description.substring(0, 60)}{req.description.length > 60 ? '...' : ''} 
                    ({req.status === 'new' ? 'Новый' : 'В работе'})
                  </option>
                ))}
              </select>
              {formData.request_id && (
                <div style={{
                  marginTop: 'var(--space-s)',
                  padding: 'var(--space-s) var(--space-m)',
                  backgroundColor: '#22c55e15',
                  borderLeft: '3px solid #22c55e',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-s)'
                }}>
                  <MessageSquare size={14} style={{ color: '#22c55e' }} />
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: '0.8125rem', color: '#15803d' }}>
                    После создания запрос будет отмечен как выполненный
                  </span>
                </div>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={loading || !formData.name}
            style={{
              width: '100%',
              padding: '16px',
              backgroundColor: 'var(--black)',
              color: 'var(--white)',
              border: 'none',
              fontFamily: 'var(--font-body)',
              fontSize: '0.8125rem',
              letterSpacing: '1px',
              textTransform: 'uppercase',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1
            }}
          >
            {loading ? 'Создание...' : 'Создать образ'}
          </button>
        </form>
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
};

export default AddLookModal;
